import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from '../../lib/motion';
import { Category } from '../../types';
import { categoryService } from '../../services/category.service';
import { HeroComposer } from '../../components/home/HeroComposer';
import { useViewModeStore } from '../../store/viewMode.store';
import { useAuthStore } from '../../store/auth.store';
import { SEO } from '../../components/SEO';
import { SchemaOrg, generateWebSiteSchema, generateOrganizationSchema, generateLocalBusinessSchema } from '../../components/SchemaOrg';
import { Shield, Users, Zap, Wrench, Calculator, BadgeCheck, HandCoins, MousePointerClick, Plus, ShieldCheck, MapPin } from 'lucide-react';
import { WhatsAppIcon } from '../../components/icons/BrandIcons';
import { buildWhatsAppUrl } from '../../utils/whatsapp';
import { localizePath, normalizeLanguage } from '../../utils/languageRouting';
import { CITY_CONTENT, KNOWN_CITY_SLUGS } from '../../data/cityContent';
import { KNOWN_SERVICE_SLUGS } from '../../data/serviceContent';
import { PrivateLink } from '../../components/common/PrivateLink';
import { getCategoryIcon, getCategoryColor } from '../../utils/categoryIcons';

const POPULAR_CATEGORY_SLUGS = [
    'plomberie',
    'electricite',
    'platrerie-staff',
    'peinture',
    'nettoyage',
    'menuiserie',
    'climatisation',
    'demenagement'
];

const POPULAR_CATEGORY_COUNT = 8;

const STEP_IMAGES = ['step_01_post_v2', 'step_02_compare_v2', 'step_03_done_v2'];

const FAQ_KEYS = [1, 2, 3, 4] as const;

/** Section header: title on the left, one line of context under it. Every section uses the same two sizes. */
const SectionHeading: React.FC<{ title: string; intro?: string; aside?: React.ReactNode }> = ({ title, intro, aside }) => (
    <div className="mb-10 flex flex-col gap-4 md:mb-12 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
            <h2 className="text-3xl font-bold tracking-tight text-balance md:text-4xl">{title}</h2>
            {intro && <p className="mt-3 text-lg text-muted-foreground">{intro}</p>}
        </div>
        {aside}
    </div>
);

export const HomePage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { mode } = useViewModeStore();
    const user = useAuthStore((state) => state.user);
    const language = normalizeLanguage(i18n.language);
    // Only public SEO roots are localized; app routes such as /jobs/create stay unprefixed.
    const localized = (path: string) => localizePath(path, language);
    // First render uses the prerendered catalogue when there is one (no swap after load); it is refreshed below.
    const [prerenderedCategories] = useState(() => categoryService.peekAllCategories());
    const [categories, setCategories] = useState<Category[]>(prerenderedCategories ?? []);
    const [isLoading, setIsLoading] = useState(!prerenderedCategories);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await categoryService.getAllCategories();
                setCategories(data ?? []);
            } catch (error) {
                console.error('Failed to load categories:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadCategories();
    }, []);

    // Popular slugs first, then fill with other categories so the grid never has holes
    // when a slug is missing from the catalogue.
    const popularCategories = [
        ...POPULAR_CATEGORY_SLUGS
            .map((slug) => categories.find((category) => category.slug === slug))
            .filter((category): category is Category => Boolean(category)),
        ...categories.filter((category) => !POPULAR_CATEGORY_SLUGS.includes(category.slug)),
    ].slice(0, POPULAR_CATEGORY_COUNT);

    const categoryName = (category: Category) =>
        category.names?.[i18n.language] || category.names?.fr || category.slug;

    const trustItems = [
        { icon: HandCoins, label: t('home.trustFree') },
        { icon: BadgeCheck, label: t('home.trustVerified') },
        { icon: MousePointerClick, label: t('home.trustChoose') },
        { icon: WhatsAppIcon, label: t('home.trustSupport') },
    ];

    const steps = [
        { title: t('home.postYourNeed'), desc: t('home.postNeedDesc'), img: STEP_IMAGES[0], alt: t('home.stepImageAlt1') },
        { title: t('home.compareOffers'), desc: t('home.compareOffersDesc'), img: STEP_IMAGES[1], alt: t('home.stepImageAlt2') },
        { title: t('home.smileItsDone'), desc: t('home.smileDesc'), img: STEP_IMAGES[2], alt: t('home.stepImageAlt3') },
    ];

    const promises = [
        { icon: Shield, title: t('home.rigorousVerification'), text: t('home.rigorousVerificationDesc') },
        { icon: Users, title: t('home.freeQuotesTitle'), text: t('home.freeQuotesDesc'), note: t('home.freeQuotesNote') },
        { icon: MousePointerClick, title: t('home.youChooseTitle'), text: t('home.youChooseDesc') },
        { icon: WhatsAppIcon, title: t('home.supportTitle'), text: t('home.supportDesc'), support: true },
    ];

    const faqs = FAQ_KEYS.map((i) => ({ q: t(`home.faqQuestion${i}`), a: t(`home.faqAnswer${i}`) }));

    // The provider view needs a real provider account: the stored switch alone must not hide
    // the composer from visitors or clients (a stale 'provider' value survives logout).
    const isClient = !(mode === 'provider' && user?.isProvider);

    return (
        <div className="relative font-sans text-foreground selection:bg-primary/20">
            <SEO
                title={t('seo.homeTitle')}
                description={t('seo.homeDescription')}
                canonical="/"
            />
            <SchemaOrg schema={[
                generateWebSiteSchema(),
                generateOrganizationSchema(),
                generateLocalBusinessSchema(),
            ]} />

            {/* Hero: the sentence to complete on the left, the trade on the right. */}
            <section className="pt-8 pb-16 md:pt-14 md:pb-20 lg:pt-16">
                <div className="mx-auto max-w-6xl">
                    <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="lg:col-span-7"
                        >
                            <p className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground">
                                <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
                                {t('home.availableInMorocco')}
                            </p>
                            <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-balance sm:text-5xl lg:text-[3.5rem] xl:text-6xl">
                                {t('home.heroTitle')} {t('home.heroTitleHighlight')}
                            </h1>
                            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                                {t('home.heroSubtitle')}
                            </p>

                            {isClient ? (
                                <div className="mt-8 md:mt-10">
                                    <HeroComposer />
                                </div>
                            ) : (
                                <div className="mt-8">
                                    <Link
                                        to="/provider/dashboard"
                                        className="inline-flex h-14 items-center justify-center rounded-2xl bg-primary px-8 text-lg font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                                    >
                                        {t('nav.providerDashboard')}
                                    </Link>
                                </div>
                            )}

                            {/* Trust strip: only promises Bricolat controls, no figures */}
                            <ul className="mt-10 grid grid-cols-1 gap-x-8 gap-y-3 text-sm text-foreground/80 sm:grid-cols-2 md:mt-12">
                                {trustItems.map(({ icon: Icon, label }) => (
                                    <li key={label} className="flex items-start gap-2.5">
                                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                                            <Icon className="h-3 w-3" />
                                        </span>
                                        {label}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        <div className="lg:col-span-5">
                            <div className="relative overflow-hidden rounded-[1.75rem] bg-muted aspect-[16/11] lg:aspect-[4/5]">
                                <picture>
                                    <source media="(max-width: 1023px)" srcSet="/images/hero_home_v2-mobile.webp" type="image/webp" />
                                    <source srcSet="/images/hero_home_v2-desktop.webp" type="image/webp" />
                                    <img
                                        src="/images/hero_home_v2-desktop.jpg"
                                        alt={t('home.heroImageAlt')}
                                        width="1536"
                                        height="1024"
                                        className="h-full w-full object-cover object-[70%_center] rtl:-scale-x-100"
                                    />
                                </picture>
                                {/* One true fact on the photo: identity documents are checked for the Verified badge. */}
                                <p className="absolute bottom-4 start-4 end-4 inline-flex w-fit max-w-full items-center gap-2 rounded-full bg-card/95 px-3.5 py-2 text-sm font-medium text-foreground shadow-lg backdrop-blur">
                                    <ShieldCheck className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                                    <span className="truncate">{t('home.verifiedById')}</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works: a real sequence, so the steps are numbered. */}
            <section className="border-t border-border py-16 md:py-24">
                <div className="mx-auto max-w-6xl">
                    <SectionHeading title={t('home.howItWorksTitle')} intro={t('home.howItWorksDesc')} />
                    <ol className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
                        {steps.map((step, index) => (
                            <li key={step.img} className="flex flex-col">
                                <div className="relative mb-6 overflow-hidden rounded-3xl bg-muted aspect-[4/3]">
                                    <picture>
                                        <source srcSet={`/images/${step.img}.webp`} type="image/webp" />
                                        <img
                                            src={`/images/${step.img}.jpg`}
                                            alt={step.alt}
                                            width="1200"
                                            height="900"
                                            className="h-full w-full object-cover"
                                            loading="lazy"
                                        />
                                    </picture>
                                </div>
                                <div className="flex items-baseline gap-3">
                                    <span className="text-3xl font-bold tabular-nums text-primary" aria-hidden="true">{index + 1}</span>
                                    <h3 className="text-xl font-semibold md:text-2xl">{step.title}</h3>
                                </div>
                                <p className="mt-2 ps-9 leading-relaxed text-muted-foreground">{step.desc}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Services: tiles are real links where a public page exists, otherwise a button to the (private) search. */}
            <section className="border-t border-border py-16 md:py-24">
                <div className="mx-auto max-w-6xl">
                    <SectionHeading
                        title={t('home.ourServices')}
                        intro={t('home.subtitle')}
                        aside={(
                            <PrivateLink
                                to="/search"
                                className="inline-flex h-11 w-fit items-center justify-center rounded-xl border border-foreground/15 bg-card px-5 text-sm font-semibold transition-colors hover:border-foreground/40"
                            >
                                {t('home.viewCatalog')}
                            </PrivateLink>
                        )}
                    />

                    {isLoading ? (
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                            {Array.from({ length: POPULAR_CATEGORY_COUNT }, (_, i) => (
                                <div key={i} className="h-32 animate-pulse rounded-2xl bg-muted" />
                            ))}
                        </div>
                    ) : (
                        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                            {popularCategories.map((category) => {
                                const tileClass = 'group flex h-full w-full flex-col items-start gap-4 rounded-2xl border border-border bg-card p-5 text-start transition-colors hover:border-primary focus-visible:border-primary md:p-6';
                                const content = (
                                    <>
                                        <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-muted ${getCategoryColor(category.slug)}`}>
                                            {getCategoryIcon(category.slug, category.iconUrl, 'w-6 h-6')}
                                        </span>
                                        <span className="text-base font-semibold leading-snug group-hover:text-primary md:text-lg">{categoryName(category)}</span>
                                    </>
                                );
                                return (
                                    <li key={category.id}>
                                        {KNOWN_SERVICE_SLUGS.includes(category.slug) ? (
                                            <Link to={localized(`/services/${category.slug}`)} className={tileClass}>{content}</Link>
                                        ) : (
                                            <PrivateLink to={`/search?categoryId=${category.id}`} className={tileClass}>{content}</PrivateLink>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </section>

            {/* Price simulator */}
            <section className="py-4 md:py-6">
                <div className="mx-auto max-w-6xl">
                    <div className="flex flex-col gap-6 rounded-3xl border border-border bg-card p-6 md:flex-row md:items-center md:gap-10 md:p-10">
                        <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <Calculator className="h-7 w-7" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{t('priceSimulator.promoTitle')}</h2>
                            <p className="mt-2 text-base text-muted-foreground md:text-lg">{t('priceSimulator.promoSubtitle')}</p>
                        </div>
                        <Link
                            to={localized('/simulateur-prix')}
                            className="inline-flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-xl border border-foreground/15 px-6 font-semibold transition-colors hover:border-foreground/40 md:w-auto"
                            data-track="home_price_simulator_cta"
                        >
                            <Zap className="h-4 w-4" aria-hidden="true" />
                            {t('priceSimulator.promoCta')}
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Bricolat: four promises that are true today, as a list rather than cards. */}
            <section className="py-16 md:py-24">
                <div className="mx-auto max-w-6xl">
                    <SectionHeading title={t('home.whyChooseUs')} intro={t('home.whyChooseUsHonestDesc')} />
                    <ul className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
                        {promises.map(({ icon: Icon, title, text, note, support }) => (
                            <li key={title} className="border-t-2 border-foreground pt-5">
                                <Icon className="h-6 w-6 text-primary" aria-hidden="true" />
                                <h3 className="mt-4 text-xl font-semibold">{title}</h3>
                                <p className="mt-2 leading-relaxed text-muted-foreground">{text}</p>
                                {note && <p className="mt-2 text-sm text-muted-foreground">{note}</p>}
                                {support && (
                                    <div className="mt-4 flex flex-col gap-2 text-sm font-semibold">
                                        <Link to={localized('/contact')} className="w-fit underline decoration-foreground/30 underline-offset-4 hover:decoration-primary">
                                            {t('home.contactSupport')}
                                        </Link>
                                        <a
                                            href={buildWhatsAppUrl(t('whatsapp.messages.support'))}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex w-fit items-center gap-2 text-green-700 underline decoration-green-700/30 underline-offset-4 hover:decoration-green-700"
                                        >
                                            {t('whatsapp.open')} <WhatsAppIcon className="h-4 w-4" title="" />
                                        </a>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Cities: internal links to the city landing pages */}
            <section className="border-t border-border py-16 md:py-24">
                <div className="mx-auto max-w-6xl">
                    <SectionHeading title={t('home.citiesTitle')} intro={t('home.citiesDesc')} />
                    <ul className="flex flex-wrap gap-x-8 gap-y-4 text-xl font-semibold md:text-2xl">
                        {KNOWN_CITY_SLUGS.map((slug) => (
                            <li key={slug}>
                                <Link
                                    to={localized(`/pro/${slug}`)}
                                    className="underline decoration-foreground/20 decoration-2 underline-offset-8 transition-colors hover:text-primary hover:decoration-primary"
                                >
                                    {CITY_CONTENT[slug].name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Provider band */}
            <section className="py-4 md:py-6">
                <div className="mx-auto max-w-6xl">
                    <div className="flex flex-col gap-6 rounded-3xl bg-foreground p-6 text-background md:flex-row md:items-center md:gap-10 md:p-10">
                        <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-400/15 text-amber-400">
                            <Wrench className="h-7 w-7" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{t('home.providerBandTitle')}</h2>
                            <p className="mt-2 text-base text-background/70 md:text-lg">{t('home.providerBandDesc')}</p>
                        </div>
                        <Link
                            to={localized('/devenir-prestataire')}
                            className="inline-flex h-12 w-full shrink-0 items-center justify-center rounded-xl bg-amber-400 px-6 font-semibold text-gray-900 transition-colors hover:bg-amber-300 md:w-auto"
                            data-track="home_provider_band_cta"
                        >
                            {t('home.becomeProvider')}
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQPage JSON-LD Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": faqs.map(({ q, a }) => ({
                            "@type": "Question",
                            "name": q,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": a
                            }
                        }))
                    })
                }}
            />

            {/* FAQ */}
            <section className="py-16 md:py-24">
                <div className="mx-auto max-w-6xl md:grid md:grid-cols-12 md:gap-10">
                    <div className="md:col-span-4">
                        <h2 className="text-3xl font-bold tracking-tight md:text-4xl">{t('home.faq')}</h2>
                        <p className="mt-3 text-lg text-muted-foreground">{t('home.faqDesc')}</p>
                    </div>
                    <div className="mt-8 border-t border-border md:col-span-8 md:mt-0">
                        {faqs.map((faq) => (
                            <details key={faq.q} className="group border-b border-border [&_summary::-webkit-details-marker]:hidden">
                                <summary className="flex cursor-pointer items-center justify-between gap-4 py-5 text-start">
                                    <h3 className="text-lg font-semibold md:text-xl">{faq.q}</h3>
                                    <Plus className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-45" aria-hidden="true" />
                                </summary>
                                <p className="pb-6 leading-relaxed text-muted-foreground">{faq.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final call: same two destinations as the hero, said once more at the end of the page. */}
            <section className="border-t border-border py-16 md:py-24">
                <div className="mx-auto max-w-6xl">
                    <h2 className="text-4xl font-bold tracking-tight text-balance md:text-5xl">{t('home.readyToStart')}</h2>
                    <p className="mt-4 max-w-2xl text-lg text-muted-foreground md:text-xl">{t('home.finalCtaDesc')}</p>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                        {isClient && (
                            <PrivateLink
                                to="/jobs/create"
                                className="inline-flex h-14 items-center justify-center rounded-2xl bg-primary px-8 text-lg font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                                data-track="final_post_request"
                            >
                                {t('home.heroCta')}
                            </PrivateLink>
                        )}
                        <PrivateLink
                            to="/search"
                            className="inline-flex h-14 items-center justify-center rounded-2xl border border-foreground/15 bg-card px-8 text-lg font-semibold transition-colors hover:border-foreground/40"
                        >
                            {t('home.findProvider')}
                        </PrivateLink>
                    </div>
                </div>
            </section>
        </div>
    );
};
